import { STATUS_BOOKED, STATUS_WAITLIST } from '../constants'

export const isWaitlistBookingStatus = status => status === STATUS_WAITLIST

export const getBookingStatusFromPayload = payload =>
  payload?.booking?.status ?? payload?.booking_status ?? null

export const isWaitlistBookingPayload = payload =>
  isWaitlistBookingStatus(getBookingStatusFromPayload(payload))

export const getSlotBookedCount = slot =>
  slot?.booked ?? slot?.booked_count ?? 0

export const getSlotMax = slot =>
  slot?.appointment_max_allowed ?? slot?.max_allowed ?? 0

export const isUserBookedOnSlot = slot => slot?.booking_status === STATUS_BOOKED

export const isUserWaitlistedOnSlot = slot =>
  slot?.booking_status === STATUS_WAITLIST

export const isSlotFull = slot =>
  getSlotMax(slot) > 0 && getSlotBookedCount(slot) >= getSlotMax(slot)

export const isQuickPayOnlyAppointment = appointment => {
  if (!appointment) return false
  if (appointment.price_type === 1 || appointment.price_type === 'free') {
    return false
  }
  const rawPrice = appointment.price ?? appointment.service_data?.price
  if (rawPrice == null || rawPrice === '' || rawPrice === 'invalid') {
    return false
  }
  const price = Number(rawPrice)
  const credits = Number(
    appointment.credits ?? appointment.service_data?.credits ?? 0
  )
  return price > 0 && credits <= 0
}

export const isFreeAppointment = (appointment, slot = null) => {
  if (appointment?.price_type === 1 || appointment?.price_type === 'free') {
    return true
  }
  const rawPrice =
    appointment?.price ?? slot?.price ?? appointment?.service_data?.price
  if (rawPrice == null || rawPrice === '' || rawPrice === 'invalid') {
    return false
  }
  return Number(rawPrice) === 0
}

export const isUserInvolvedInSlot = slot =>
  isUserBookedOnSlot(slot) || isUserWaitlistedOnSlot(slot)

export const shouldHideFullQuickPaySlot = (slot, appointment) => {
  if (!isQuickPayOnlyAppointment(appointment)) return false
  if (isUserInvolvedInSlot(slot)) return false
  return isSlotFull(slot)
}

export const isWaitlistJoinable = (slot, waitlistEnabled, appointment = null) =>
  waitlistEnabled &&
  !isQuickPayOnlyAppointment(appointment) &&
  slot?.is_waitlisted &&
  slot?.waitlist > 0 &&
  !isUserWaitlistedOnSlot(slot) &&
  !isUserBookedOnSlot(slot) &&
  isSlotFull(slot)

export const isAppointmentWaitlistContext = (
  slot,
  waitlistEnabled,
  appointment = null
) =>
  isUserWaitlistedOnSlot(slot) ||
  isWaitlistJoinable(slot, waitlistEnabled, appointment)

export const isJoiningWaitlistCheckout = (
  slot,
  waitlistEnabled,
  appointment = null
) => isWaitlistJoinable(slot, waitlistEnabled, appointment)

export const isSpotAvailableAfterWaitlist = slot =>
  isUserWaitlistedOnSlot(slot) && !isSlotFull(slot)

export const isStillOnWaitlist = slot =>
  isUserWaitlistedOnSlot(slot) && isSlotFull(slot)

export const isSlotListingDisabled = (
  slot,
  waitlistEnabled,
  appointment = null
) => {
  if (shouldHideFullQuickPaySlot(slot, appointment)) {
    return true
  }
  if (isUserBookedOnSlot(slot) || isUserWaitlistedOnSlot(slot)) {
    return false
  }
  if (!waitlistEnabled) {
    return isSlotFull(slot)
  }
  if (isWaitlistJoinable(slot, waitlistEnabled, appointment)) {
    return false
  }
  return isSlotFull(slot) && (slot?.waitlist ?? 0) === 0
}

export const getWaitlistOrdinal = n => {
  if (!n || n <= 0) return null
  const suffix = n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th'
  return `You're ${n}${suffix} in waitlist`
}

export const getSlotStatusDisplay = (
  slot,
  waitlistEnabled,
  showWaitlistNumber = false,
  appointment = null
) => {
  if (!slot) return null

  const max = getSlotMax(slot)
  const remaining = Math.max(0, max - getSlotBookedCount(slot))

  if (isUserBookedOnSlot(slot)) {
    return {
      type: 'booked',
      text: 'Booked',
      showDot: true,
      dotVariant: 'green'
    }
  }

  if (isSpotAvailableAfterWaitlist(slot)) {
    return {
      type: 'spotAvailable',
      text: 'Spot available',
      showDot: true,
      dotVariant: 'brand'
    }
  }

  if (isStillOnWaitlist(slot)) {
    const ordinal =
      showWaitlistNumber && slot.waitlist_number > 0
        ? getWaitlistOrdinal(slot.waitlist_number)
        : null
    return {
      type: 'waitlistJoined',
      text: ordinal || 'Waitlist joined',
      showDot: true,
      dotVariant: 'grey'
    }
  }

  if (remaining > 0 && slot?.booking_status == null) {
    return {
      type: 'available',
      text: `${remaining}/${max} available`,
      showDot: true,
      dotVariant: 'green'
    }
  }

  if (isWaitlistJoinable(slot, waitlistEnabled, appointment)) {
    return {
      type: 'waitlist',
      text: 'Join waitlist',
      showDot: true,
      dotVariant: 'grey'
    }
  }

  if (isSlotFull(slot) && (slot?.waitlist ?? 0) === 0) {
    return {
      type: 'full',
      text: 'Full',
      showDot: true,
      dotVariant: 'grey'
    }
  }

  return null
}

export const filterAppointmentSlots = (
  slots,
  waitlistEnabled,
  appointment = null
) => {
  if (!slots?.length) return slots ?? []
  return slots.filter(
    slot =>
      !shouldHideFullQuickPaySlot(slot, appointment) &&
      (waitlistEnabled
        ? !isSlotFull(slot) ||
          isUserInvolvedInSlot(slot) ||
          isWaitlistJoinable(slot, waitlistEnabled, appointment)
        : getSlotBookedCount(slot) < getSlotMax(slot))
  )
}
